import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, EmptyState, ErrorAlert, Field, PageHeader, Spinner } from '../components/ui';
import { formatDate, truncate } from '@kontentowo/shared';

export function BrandBrainPage() {
  const { data: brands } = useAsync(() => api.brands.list(), []);
  const [brandId, setBrandId] = useState('');
  const sources = useAsync(() => api.sources(brandId || undefined), [brandId]);
  const documents = useAsync(() => api.brandBrain.documents(brandId || undefined), [brandId]);

  const [crawling, setCrawling] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);

  const [manualUrl, setManualUrl] = useState('');
  const [manualName, setManualName] = useState('');

  const crawlSource = async (sourceId: string, name: string) => {
    setCrawling(sourceId);
    setActionError(null);
    setNote(null);
    try {
      const res = await api.brandBrain.crawlSource(sourceId);
      setNote(res.error ? `Błąd: ${res.error}` : `Zacrawlowano „${name}" (${res.chars} znaków, ${res.documents} dok.).`);
      documents.reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setCrawling(null);
    }
  };

  const crawlManual = async () => {
    if (!effectiveBrand() || !manualUrl) return;
    setCrawling('manual');
    setActionError(null);
    setNote(null);
    try {
      const doc = await api.brandBrain.crawlUrl({ brandId: effectiveBrand(), url: manualUrl, name: manualName || undefined });
      setNote(`Zapisano dokument „${doc.title ?? manualUrl}" (${doc.chunkCount} chunków).`);
      setManualUrl('');
      setManualName('');
      documents.reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setCrawling(null);
    }
  };

  const removeDoc = async (id: string) => {
    if (!confirm('Usunąć dokument?')) return;
    try {
      await api.brandBrain.deleteDocument(id);
      documents.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  const effectiveBrand = () => brandId || brands?.[0]?.id || '';

  return (
    <>
      <PageHeader title="Brand Brain" subtitle="Crawler (simple HTTP) + dokumenty marki. Docelowo: embeddings → Vectorize → RAG." />

      <ErrorAlert error={actionError} />
      {note && <div className="alert alert--success">{note}</div>}

      <Card title="Marka / źródła">
        <Field label="Marka">
          <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            <option value="">— wszystkie marki —</option>
            {brands?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>

        {sources.loading && <Spinner />}
        {sources.data && sources.data.length === 0 && <p className="muted">Brak źródeł dla tej marki.</p>}
        {sources.data && sources.data.length > 0 && (
          <div className="stack">
            {sources.data.map((s) => (
              <div key={s.id} className="inline" style={{ justifyContent: 'space-between' }}>
                <div>
                  <Badge tone="neutral">{s.type}</Badge> <span style={{ fontWeight: 550 }}>{s.name}</span>
                  <div className="muted" style={{ fontSize: 12 }}>{s.url}</div>
                </div>
                <Button size="sm" variant="primary" disabled={crawling === s.id || !s.url} onClick={() => crawlSource(s.id, s.name)}>
                  {crawling === s.id ? 'Crawling…' : 'Crawl'}
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="divider" />
        <Field label="Crawl ad-hoc (URL)">
          <div className="form-row">
            <input className="input" value={manualUrl} onChange={(e) => setManualUrl(e.target.value)} placeholder="https://example.com/…" />
            <input className="input" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Nazwa (opcjonalnie)" />
          </div>
        </Field>
        <Button variant="primary" onClick={crawlManual} disabled={crawling === 'manual' || !effectiveBrand() || !manualUrl}>
          {crawling === 'manual' ? 'Crawling…' : 'Crawl URL'}
        </Button>
      </Card>

      <div style={{ marginTop: 20 }}>
        <h2>Dokumenty</h2>
        {documents.loading && <Spinner />}
        {documents.error ? <ErrorAlert error={documents.error} /> : null}
        {documents.data && documents.data.length === 0 && <EmptyState icon="◉" title="Brak dokumentów" hint="Przeskanuj źródło lub wpisz URL." />}
        {documents.data && documents.data.length > 0 && (
          <Card flush>
            <table className="table">
              <thead>
                <tr>
                  <th>Tytuł</th>
                  <th>Brand</th>
                  <th>Znaki</th>
                  <th>Chunki</th>
                  <th>Utworzono</th>
                  <th style={{ textAlign: 'right' }}>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {documents.data.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <details>
                        <summary style={{ cursor: 'pointer', fontWeight: 550 }}>{truncate(d.title ?? 'Bez tytułu', 60)}</summary>
                        <div className="pre" style={{ marginTop: 8, maxHeight: 160, overflowY: 'auto' }}>{truncate(d.content, 600)}</div>
                      </details>
                    </td>
                    <td className="muted">{brands?.find((b) => b.id === d.brandId)?.name ?? d.brandId}</td>
                    <td className="muted">{d.content.length}</td>
                    <td>
                      <Badge tone="neutral">{d.chunkCount}</Badge>
                    </td>
                    <td className="muted">{formatDate(d.createdAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Button size="sm" variant="danger" onClick={() => removeDoc(d.id)}>
                        Usuń
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </>
  );
}